import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T, TF } from "react-translator-component";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../redux/store";
import {
  initialErrors,
  reset,
  setErrors,
  setItem,
  setPrivateUsers,
  setVoteOptions
} from "../redux/Reducers/createPostReducer"
import dayjs from "../util/dayjs";
import { Dayjs } from "dayjs";
import {
  ParticipantSettingState,
  PrivateUser,
  Tag,
  UserEntity,
  VoteOption,
  type Errors
} from "../util/CreatePost/types"
import {
  initialApplicationErrors,
  initialVoteSettingErrors,
  isSamePrivateUser,
  MAX_VOTE_OPTIONS_COUNT,
  MEMBER_PARTICIPATE,
  MIN_VOTE_OPTIONS_COUNT
} from "../util/CreatePost/utils";
import { postalCodeValidate } from "../util/CreatePost/validator";
import {
  isRegularAppUser,
  isPrivateGroup,
  isGroupLeader
} from "../util/utils";
import { pushMemberGroupPost, pushUserPost, pushUserPostTag } from "../services/gtm";
import appServices from "../services/app-services/appServices";

import Radios from "../components/Radios";
import GCBackButton from "../components/GreenCity/GCBackButton";
import LoadingModal from "../components/LoadingModal";
import SubmitButton from "../components/SubmitButton";
import Section from "../components/CreatePost/Section";
import Separator from "../components/Separator";
import GroupIconImage from "../components/GreenCity/GroupIconImage";
import Category from "../components/CreatePost/Category";
import Group from "../components/CreatePost/Group";
import SelectMembers from "../components/CreatePost/SelectMembers";
import SelectPostType from "../components/CreatePost/SelectPostType";
import SelectImages from "../components/CreatePost/SelectImages";
import SelectAttachments from "../components/CreatePost/SelectAttachments";
import Title from "../components/CreatePost/Title";
import Details from "../components/CreatePost/Details";
import EventTerm from "../components/CreatePost/EventTerm";
import EventLocation from "../components/CreatePost/EventLocation";
import EventUrl from "../components/CreatePost/EventUrl";
import EventApplication from "../components/CreatePost/EventApplication";
import Tags from "../components/CreatePost/Tags";
import VoteSetting from "../components/CreatePost/VoteSetting";
import NoticeBanner from "../components/NoticeBanner";
import { ErrorMessageModal } from "../components/ErrorMessageModal";

import {
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import communityLogoImage from "../assets/sliderimages/community_logo.png"
import { colors } from "../common/colors";
import { styles as commonStyles } from "../components/CreatePost/CreatePostStyles";

export default function CreatePost(): ReactElement {
  const today = useMemo(() => dayjs(), []);
  const voteDeadline: Dayjs = today.add(90, "days")

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user } = useSelector((state: any) => state.userAuth);
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const createPostType = useSelector((state: any) => state.createPost.createPostType);
  const post = useSelector((state: any) => state.createPost.values);

  const locationState = useLocation().state;
  const isGroupPost : boolean = ['GroupPublicPost', 'InsideGroupPost', 'GroupPrivateUserPost', 'GroupEventPost'].includes(createPostType)

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [eventDatehasPassed, setEventDatehasPassed] = useState<boolean>(false);
  const [postCategories, setPostCategories] = useState<any>([]);
  const [postGroups, setPostGroups] = useState<any>([]);
  const [agree, setAgree] = useState<boolean>(false);

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [displayNotice, setDisplayNotice] = useState<boolean>(false)
  const [apiMessage, setApiMessage] = useState<string[]>([]);
  const showErrorDialog = useMemo(() => apiMessage.length > 0, [apiMessage]);

  const members = useRef<any>([]);
  const displayNoticeTime = 3000

  const apiCalls = async () => {
    try {
      setIsLoading(true)

      const categories = await appServices.getCategory()
      setPostCategories(categories)

      const groups = await appServices.getGroups()
      setPostGroups(groups)

      if (!groupInfo) {
        return
      }
      if (!['InsideGroupPost', 'GroupPrivateUserPost', 'GroupEventPost'].includes(createPostType)) {
        return
      }
      const memberGroupUsers = await appServices.getMemberList(groupInfo.id)
      const mem = memberGroupUsers?.member_group_users.filter((member: any) => member.user.id !== user?.id)
      if (!isGroupLeader(groupInfo, user)) {
        mem.unshift({ user: groupInfo.owner, role: "leader" })
      }
      members.current = mem

      if (post.id) return

      if ('InsideGroupPost' === createPostType || ('GroupEventPost' === createPostType && isPrivateGroup(groupInfo.groupIsPublic))) {
        let users: PrivateUser[] = mem.map((member: any) => {
          const memberUser = member.user
          return {
            userId: memberUser.id,
            name: memberUser.name,
            nickname: memberUser.nickname,
            image: memberUser.image,
            userType: memberUser.class_name,
            _destroy: 0,
            identifier: crypto.randomUUID()
          }
        })

        users.push({
          userId: user.id,
          name: user.name,
          nickname: user.nickname,
          image: user.image,
          userType: user.class_name,
          _destroy: 0,
          identifier: crypto.randomUUID()
        })
        dispatch(setPrivateUsers({value: users}))
      }
    } catch (error: any) {
      setApiMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
    } finally {
      setIsLoading(false)
    }
  }

  // 初期化処理 (データ依存なし)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // 初期化処理 (API Call)
  useEffect(() => {
    if (!locationState) return;
    apiCalls()

    if (post.event.endDate) {
      setEventDatehasPassed(post.event.endDate.isBefore(today))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState, groupInfo])

  // 初期化処理 (この投稿を見られるユーザー)
  useEffect(() => {
    if (!locationState) return

    const initialUsers = locationState.initialPrivateUsers

    switch(createPostType) {
      case 'UserPost': {
        if (initialUsers.length > 0) {
          break
        }
        return
      }
      case 'GroupPublicPost': {
        return
      }
      case 'InsideGroupPost':
      case 'GroupPrivateUserPost': {
        break
      }
      case 'GroupEventPost': {
        if (!isPrivateGroup(groupInfo?.groupIsPublic)) {
          return
        }
        break
      }
    }

    if (!post.id && initialUsers.length > 0) {
      const privateUsers: PrivateUser[] = initialUsers.map((initUser: any) => {
        return {
          userId: initUser.id,
          name: initUser.name,
          nickname: initUser.nickname,
          image: initUser.image,
          type: initUser.class_name,
          _destroy: 0
        }
      })
      dispatch(setPrivateUsers({value: privateUsers}))
    }
    dispatch(setItem({key: 'isPrivate', value: true}))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState, groupInfo])

  const validateForm = () => {
    const newErrors: Errors = Object.assign({}, JSON.parse(JSON.stringify(initialErrors))); // ディープコピー

    if (!post.title) newErrors.title = `${T("title")}${T("error_enter")}`;

    if (createPostType !== 'GroupEventPost') {
      if (!post.details) newErrors.details = `${T("details")}${T("error_enter")}`;
      if (!post.category) newErrors.category = `${T("post_category")}${T("error_select")}`;
      if (!post.group) newErrors.group = `${T("group")}${T("error_select")}`;
    }

    if (post.acceptVotes) {
      const voteOptions = post.voteSetting.voteOptions.filter((option: VoteOption) => option._destroy === 0)
      const voteSettingError = initialVoteSettingErrors(voteOptions.length)
      voteOptions.forEach((option: any, index: number) => {
        if (!option.optionText || option.optionText.trim() === "") {
          voteSettingError.voteOptions[index].optionText = `${T("post_vote_option")}${T("error_enter")}`;
        }
      })
      if (voteDeadline === undefined) voteSettingError.expiryDate = `${T("end_date")}${T("error_enter")}`;
      if (post.voteSetting.voteQuestion === "") voteSettingError.voteQuestion = `${T("post_vote_question")}${T("error_enter")}`;
      if (voteOptions.length < MIN_VOTE_OPTIONS_COUNT) voteSettingError.limitVoteOptions = TF(T("error_min_vote_option_count"), MIN_VOTE_OPTIONS_COUNT);
      if (voteOptions.length > MAX_VOTE_OPTIONS_COUNT) voteSettingError.limitVoteOptions = TF(T("error_max_vote_option_count"), MAX_VOTE_OPTIONS_COUNT);
      newErrors.voteSetting = voteSettingError
    }

    if (createPostType === 'GroupEventPost') {
      if (!post.event.venueName) newErrors.event.venueName = `${T("venue_name")}${T("error_enter")}`;
      if (!post.event.postalCode) {
        newErrors.event.postalCode = `${T("postal_code")}${T("error_enter")}`;
      } else {
        const valueCheck = postalCodeValidate(post.event.postalCode)
        if (!valueCheck.valid) newErrors.event.postalCode = valueCheck.error
      }
      if (!post.event.address) newErrors.event.address = `${T("address")}${T("error_enter")}`;

      if (isStartDateAfterEndDate(post.event.startDate, post.event.endDate)) {
        newErrors.event.endDate = TF(T("error_date_after"), T("end_date"), T("start_date"));
      }
    }

    const applicationSections = post.event.applicationSetting.sections.filter((option: VoteOption) => option._destroy === 0)
    const sectionsErrors = initialApplicationErrors(applicationSections.length).sections
    if (post.event.acceptApplication) {
      if (isStartDateAfterEndDate(post.event.applicationSetting.startDate! ,post.event.endDate)) {
        newErrors.event.applicationSetting.startDate = TF(T("error_date_before"), T("application_start_date"), T("end_date"));
      }
      if (isStartDateAfterEndDate(post.event.applicationSetting.startDate! ,post.event.applicationSetting.endDate!)) {
        newErrors.event.applicationSetting.endDate = TF(T("error_date_after"), T("application_end_date"), T("application_start_date"));
      } else if (isStartDateAfterEndDate(post.event.applicationSetting.endDate!, post.event.endDate)) {
        newErrors.event.applicationSetting.endDate = TF(T("error_date_before"), T("application_end_date"), T("end_date"));
      }

      applicationSections.forEach((section: ParticipantSettingState, index: number) => {
        const { name, feeAmount, numberOfParticipants, free } = section;

        if (!name || name?.trim() === '') {
          sectionsErrors[index].name = `${T("participation_item_name")}${T("error_set_value")}`;
        }
        if (!free && (!feeAmount || String(feeAmount).trim() === "")) {
          sectionsErrors[index].feeAmount = `${T("participation_fee")}${T("error_set_value")}`;
        }
        if (!numberOfParticipants || String(numberOfParticipants).trim() === '') {
          sectionsErrors[index].numberOfParticipants = `${T("number_of_participants")}${T("error_select")}`;
        }
      });
    }

    newErrors.event.applicationSetting.sections = sectionsErrors
    dispatch(setErrors(newErrors));

    return !hasFormError(newErrors);
  };

  const isStartDateAfterEndDate = (startDate: Dayjs, endDate: Dayjs) => {
    const endDay = dayjs(endDate).startOf('day');
    const startDay = dayjs(startDate).startOf('day');
    return startDay.isAfter(endDay)
  }

  const hasFormError = (obj: any): boolean => {

    return Object.values(obj).some((item: any) => {
      if (typeof item === "string") {
        return item !== ""
      } else {
        return hasFormError(item)
      }
    })
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo(0, 0);
      return false
    };

    setIsLoading(true);

    let postPrivateUsersAttributes: any = null
    if (post.isPrivate) {
      postPrivateUsersAttributes = []
      if (!post.privateUsers.some((privateUser: PrivateUser) => isSamePrivateUser(privateUser, user.class_name, user.id))) {
        postPrivateUsersAttributes.push({
          privater_id: user.id,
          privater_type: user.class_name,
        })
      }

      post.privateUsers.forEach((privateUser: PrivateUser) => {
        postPrivateUsersAttributes.push({
          id: privateUser.id,
          privater_id: privateUser.userId,
          privater_type: privateUser.userType,
          _destroy: privateUser._destroy
        })
      })
    }

    const voteOptionsAttributes = post.voteSetting.voteOptions.flatMap((option : VoteOption) => {
      if (!post.acceptVotes && !option.id) return []

      return [{
        id: option.id,
        option_text: option.optionText,
        _destroy: !post.acceptVotes ? 1 : option._destroy,
      }]
    });

    let postEventsAttributes: any[] = []
    if (createPostType === 'GroupEventPost') {
      const createParticipantSettings = (): any[] => {
        return post.event.applicationSetting.sections.map((section: ParticipantSettingState) => {
          return {
            id: section.id,
            name: section.name,
            number_of_participants: section.numberOfParticipants,
            fee_amount: section.free ? null : section.feeAmount,
            free: section.free,
            _destroy: section._destroy,
          }
        })
      }

      // イベント 応募ボタン
      let eventParticipantSettings: any[] = [];
      const applicationSections: ParticipantSettingState[] = post.event.applicationSetting.sections
      if (post.event.acceptApplication) {
        eventParticipantSettings = createParticipantSettings();
        const idList = applicationSections.flatMap((section: ParticipantSettingState) => section.id ?? []);
        applicationSections.forEach((section: ParticipantSettingState) => {
          if (section.id && !idList.includes(section.id)) {
            eventParticipantSettings.push({
              id: section.id,
              _destroy: 1
            })
          }
        })
      } else if (applicationSections.length > 0 && !post.event.acceptApplication) {
        // 設置する → 設置しない への変更のため全ての応募項目を削除する
        eventParticipantSettings = applicationSections
          .filter((section: any) => !isNaN(section?.id))
          .map((section: any) => {
            return {
              id: section.id,
              _destroy: 1
            }
          })
      }

      let postalCode = post.event.postalCode
      postalCode = postalCode.slice(0, 3) + "-" + postalCode.slice(3);

      postEventsAttributes = [{
        id: post.event?.id ?? "",
        title: post.title,
        description: post.event.description,
        start_date: dayjs(post.event.startDate).format("YYYY-M-D"),
        end_date: dayjs(post.event.endDate).format("YYYY-MM-DD"),
        venue_name: post.event.venueName,
        postal_code: postalCode,
        address: post.event.address,
        url: post.event.url,
        member_participation_rule: MEMBER_PARTICIPATE,
        application_start_date: dayjs(post.event.applicationSetting.startDate).format("YYYY-M-D"),
        application_end_date: dayjs(post.event.applicationSetting.endDate).format("YYYY-M-D"),
        application_cancellation_rule: post.event.applicationCancelRule,
        // イベント投稿画面 投票の質問
        voting_question: post.acceptVotes ? post.voteSetting.voteQuestion : "",
        post_event_participant_settings_attributes: eventParticipantSettings,
      }]
    }

    const tagAttributes = post.tags.map((tag: Tag) => {
      return {
        id: tag.id,
        tag: tag.tag,
        _destroy: tag._destroy === "1" ? 1 : null,
      }
    })

    const body = {
      post: {
        is_private: post.isPrivate,
        post_private_users_attributes: postPrivateUsersAttributes,
        title: post.title,
        details: post.details,
        member_group_id: isGroupPost ? groupInfo.id : null,
        group_id: createPostType === 'GroupEventPost' ? 1 : Number(post.group),
        category_id: createPostType === 'GroupEventPost' ? 1 : Number(post.category),
        expiry_date: post.acceptVotes ? dayjs(post.voteSetting.expiryDate)?.format("YYYY-M-D") : "",
        is_anonymous: post.showNameOption,
        enable_comments: post.acceptComments,
        enable_participants: post.acceptVotes,
        post_events_attributes: postEventsAttributes,
        vote_options_attributes: voteOptionsAttributes,
        // みんなの投稿、グループ内の投稿画面(みんなの投稿、グループ内の投稿、グループ内のプライベート投稿)
        // 投票の質問
        vote_question: createPostType === 'GroupEventPost' || !post.acceptVotes ? "" : post.voteSetting.voteQuestion,
        images_attributes: post.images,
        attachments_attributes: post.attachments,
        tags_attributes: tagAttributes,
        is_private_group_user: createPostType === 'GroupPrivateUserPost'
      }
    };

    try {
      let res: any;
      if (post?.id) {
        res = await appServices.editPost(post.id, body);
        if (!isGroupPost) {
          pushUserPostTag(user, post.isPrivate, tagAttributes);
        }
      } else {
        res = await appServices.savePost(body);
        if (isGroupPost) {
          pushMemberGroupPost(user, groupInfo, createPostType, tagAttributes);
        } else if (isRegularAppUser(user)) {
          pushUserPost(user, post.isPrivate);
          pushUserPostTag(user, post.isPrivate, tagAttributes);
        }
      }
      handleSuccess(res)
      return true
    } catch (error: any) {
      setApiMessage(error?.response?.data?.message ?? [T("api_unknown_error")]);
      if (error?.response?.data?.error_key?.includes("already_voted")) {
        recyclePostData()
      }
      return false
    } finally {
      setIsLoading(false);
    }
  };

  const recyclePostData = async () => {
    if (!post?.id) return

    // 投票に変更があった場合、元に戻す
    try {
      setIsLoading(true);
      const res = await appServices.getSinglePost(post.id)
      const resVoteOption = res.post.vote_options
      const newVoteOptions: VoteOption[] = Object.assign([], JSON.parse(JSON.stringify(post.voteSetting.voteOptions)));

      resVoteOption.forEach((resOption: any) => {
        const voteOption = {
          id: resOption.id,
          optionText: resOption.option_text,
          isVoted: true,
          key: crypto.randomUUID(),
          _destroy: 0,
        }

        // 投票設定は変えていない時
        if (post.acceptVotes) {
          const index = newVoteOptions.findIndex((option: VoteOption) => option?.id === resOption.id)
          if (index >= 0) {
            newVoteOptions[index] = voteOption
          } else {
            newVoteOptions.push(voteOption)
          }
        } else  {
          if (newVoteOptions.some((option: VoteOption) => option?.id === resOption.id)) {
            const index = newVoteOptions.findIndex((option: VoteOption) => option?.id === resOption.id)
            newVoteOptions[index] = voteOption
          } else {
            newVoteOptions.push(voteOption)
          }
        }
      })

      newVoteOptions.sort((a: VoteOption, b: VoteOption) => {
        if (a?.id && b?.id) {
          return a.id - b.id
        } else if (a?.id && !b?.id) {
          return -1
        } else {
          return 1
        }
      })

      dispatch(setItem({key: 'acceptVotes', value: true}))
      dispatch(setVoteOptions({value: newVoteOptions}))
    } catch (error) {
      // pass
    } finally {
      setIsLoading(false);
    }
  }

  const handleSuccess = (res: any) => {
    if (res?.data?.success || (createPostType === 'GroupEventPost' && !post?.id && res?.success)) {
      const isApprovedPost = res?.data?.post?.status === "Approved" || res?.post?.status === "Approved"

      const desc = T(isApprovedPost ? "post_save_success" : "include_ng_words")
      setSuccessMessage(desc);
      setDisplayNotice(true)

      setTimeout(() => {
        if (createPostType === 'GroupEventPost') {
          navigate(-2);
        } else if (isGroupPost) {
          navigate(-1);
        } else {
          navigate("/categories");
        }
        dispatch(reset())
      }, displayNoticeTime);
    }
  }

  const getTitle = () => {
    let title = "";
    switch(createPostType) {
      case 'GroupPublicPost': {
        title = T("everyone_post")
        break
      }
      case 'InsideGroupPost': {
        title = T("inside_group_post")
        break
      }
      case 'GroupPrivateUserPost': {
        title = T("group_private_post")
        break
      }
      case 'GroupEventPost': {
        title = T("event_post")
        break
      }
    }

    if (post?.id) title = `${T("edit")} ${title}`;

    return title;
  };

  const handleNavigateEventCancelDetails = () => {
    if (!post.id) return
    navigate("/eventcanceldetails", {
      state: { postId: post.id },
    })
  }

  const handleAddPrivateUser = (newUser: any) => {
    let newPrivateUsers: PrivateUser[] = Object.assign([], JSON.parse(JSON.stringify(post.privateUsers)));
    const isExists = post.privateUsers.some(
      (privateUser: PrivateUser) => isSamePrivateUser(privateUser, newUser.class_name, newUser.id)
    )

    if (isExists) {
      newPrivateUsers = newPrivateUsers.map((privateUser: PrivateUser) => {
        if (isSamePrivateUser(privateUser, newUser.class_name, newUser.id)) privateUser._destroy = 0
        return privateUser
      })
    } else {
      newPrivateUsers.push({
        userId: newUser.id,
        name: newUser.name,
        nickname: newUser.nickname,
        image: newUser.image ?? "",
        userType: newUser.class_name,
        _destroy: 0,
        identifier: crypto.randomUUID()
      })
    }
    dispatch(setPrivateUsers({value: newPrivateUsers}))
  }

  const handleRemovePrivateUser = (removeUser: UserEntity) => {
    const { userType, userId, identifier } = removeUser;
    if (!userType || !identifier) return;

    if (userType === user.class_name && userId === user.id) return

    const targetMember = post.privateUsers.find((privateUser: PrivateUser) => privateUser.identifier === identifier)
    let newPrivateUsers: PrivateUser[] = Object.assign([], JSON.parse(JSON.stringify(post.privateUsers)));

    if (targetMember?.id) {
      newPrivateUsers = newPrivateUsers.map((member: PrivateUser) => {
        if (member?.identifier === identifier) member._destroy = 1
        return member
      });
    } else {
      newPrivateUsers = newPrivateUsers.filter((member: PrivateUser) => member.identifier !== identifier);
    }
    dispatch(setPrivateUsers({value: newPrivateUsers}))
  }

  return (
    <div style={styles.container}>
      <GCBackButton onClick={() => navigate(-1)} />
      <LoadingModal visible={isLoading} />

      {isGroupPost &&
        <div style={styles.rowWithHorizontalPadding}>
          <GroupIconImage groupId={groupInfo.id} alt="" style={styles.groupIcon} />
          <Typography style={styles.everyonePostStyle}>{getTitle()}</Typography>
        </div>
      }

      {!isGroupPost && !isRegularAppUser(user) ? (
        <div
          style={ styles.invalidAccountMessage }
        >
          <img style={styles.introSliderImg} src={communityLogoImage} alt="" />
          <p>{T("only_registered_app_user_can_create")}</p>
        </div>
      ) : (
        <>
          <Separator sx={ styles.mt10 } color="#D3D3D3" />

          <div style={styles.formBackground}>
            {createPostType === 'GroupPrivateUserPost' && (
              <SelectMembers
                groupInfo={groupInfo}
                allMembers={members.current}
                onAdd={handleAddPrivateUser}
                onDelete={handleRemovePrivateUser}
              />
            )}

            {!isGroupPost &&
              <div style={ styles.selectUsersContainer } >
                <SelectPostType
                  isEdit={post?.id ? true : false}
                  isOnlyPrivate={locationState.initialPrivateUsers.length > 0 ? true : false}
                  onAdd={handleAddPrivateUser}
                  onDelete={handleRemovePrivateUser}
                />
              </div>
            }

            { createPostType !== 'GroupEventPost' && (
              <>
                <Category categories={postCategories}/>
                <Group groups={postGroups}/>
              </>
            )}

            <SelectImages />

            <Title />

            { createPostType === 'GroupEventPost' && (
              <>
                <EventTerm baseDate={today}/>
                <EventLocation />
              </>
            )}

            <Details />

            {createPostType === 'GroupEventPost' &&
              <EventUrl />
            }

            <SelectAttachments />

            {createPostType === 'GroupEventPost' && (
              <>
                <Typography sx={commonStyles.typographyText}>
                  {T("can_participant")}
                </Typography>
                <FormControl component="fieldset">
                  <FormLabel component="legend"></FormLabel>
                  <Radios
                    value={MEMBER_PARTICIPATE}
                    options={[{
                      value: MEMBER_PARTICIPATE,
                      label: MEMBER_PARTICIPATE,
                      desc: (
                        <>
                          <Typography>{MEMBER_PARTICIPATE}</Typography>
                          <Typography style={styles.mt30}>
                            {T("can_participant_description")}
                          </Typography>
                        </>
                      ),
                      style: commonStyles.radioLabel,
                    }]}
                    onChange={() => {}}
                  />
                </FormControl>

                <EventApplication baseDate={today} />
              </>
            )}

            <Tags />

            <VoteSetting
              minimum={MIN_VOTE_OPTIONS_COUNT}
              maximum={MAX_VOTE_OPTIONS_COUNT}
              baseDate={today}
              limitDate={voteDeadline}
            />

            <div style={{ marginTop: 40 }}>
              <Section
                title={`${T("select_accept_comments")}${T("optional")}`}
                body={
                  <Radios
                    value={post.acceptComments}
                    options={[
                      { value: true, label: T("yes") },
                      { value: false, label: T("no") },
                    ]}
                    onChange={(value) => dispatch(setItem({key: 'acceptComments', value: value === "true"}))}
                  />
                }
              />
            </div>

            {createPostType !== 'GroupEventPost' && !isGroupPost && (
              <div style={{ marginTop: 40 }}>
                <Section
                  title={
                    <>
                      {T("select_post_name")}
                      <span style={ styles.colerRed }> *&nbsp;</span><br/>
                      <span style={ styles.colerGray200 }>{T("post_name_description")}</span>
                    </>
                  }
                  body={
                    <Radios
                      value={post.showNameOption}
                      options={[
                        { value: 1, label: T("nickname") },
                        { value: 2, label: T("real_name"), disabled: !user.name },
                        { value: 3, label: T("anonymous") },
                      ]}
                      onChange={(value) => dispatch(setItem({key: 'showNameOption', value: Number(value)}))}
                    />
                  }
                />
              </div>
            )}
          </div>

          <div style={ styles.noticeContainer }>
            <Section
              title={`${T("notice")}${T("required")}`}
              body={
                <>
                  <Typography style={ styles.noticeLabel }>{T("post_notes_1")}</Typography>
                  <Typography style={ styles.noticeLabel }>{T("post_notes_2")}</Typography>
                  <Typography style={ styles.noticeLabel }>{T("post_notes_3")}</Typography>

                  <FormControlLabel
                    control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
                    color="error"
                    sx={ styles.agreementLabel }
                    label={T("agreement_post_notice")}
                  />
                </>
              }
            />
          </div>

          {/* Submit Button */}
          <SubmitButton
            color="secondary"
            sx={styles.submitButton}
            size={"large"}
            variant="outlined"
            validate={async () => agree}
            onSubmit={handleSubmit}
            endIcon={
              <FontAwesomeIcon style={ styles.postEndIcon } icon={faChevronRight} />
            }
          >
            {T("submit_post")}
          </SubmitButton>

          {createPostType === 'GroupEventPost' && post?.id && !eventDatehasPassed && (
            <Button
              color="error"
              onClick={handleNavigateEventCancelDetails}
              sx={styles.cancelSubmitButton}
              size={"large"}
              variant="outlined"
              endIcon={<ArrowForwardIosIcon sx={styles.arrowForwardIosIcon} />}
            >
              {T("cancel_event")}
            </Button>
          )}

          <NoticeBanner
            open={displayNotice}
            message={successMessage}
            type={"success"}
            style={styles.width100}
            onClose={() => setDisplayNotice(false)}
          />

          <ErrorMessageModal
            open={showErrorDialog}
            messages={apiMessage}
            onClose={() => setApiMessage([])}
          />
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    paddingTop: 90,
    overflow: "scroll",
    width: "100%",
    height: "100%",
  },

  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    marginBottom: 8,
  },

  cancelSubmitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    marginTop: -4,
    marginBottom: 8,
  },

  arrowForwardIosIcon: {
    position: "absolute" as const,
    right: 0,
    top: 0,
    margin: "10px",
  },

  rowWithHorizontalPadding: {
    padding: "12px 10px 0 10px",
    display: "flex",
    alignItems: "center",
  },

  everyonePostStyle: {
    fontSize: 16,
    fontWeight: "bold",
    fontFamily: "mplus-1c-bold",
    color: colors.farm_black,
  },

  icons: {
    fontSize: "30px",
    cursor: "pointer",
  },

  introSliderImg: {
    backgroundColor: colors.farm_white,
    height: "100%",
    width: "100%",
    maxWidth: 400,
  },

  groupIcon: {
    borderRadius: 13,
    marginRight: 10,
    height: 26,
    width: 26,
  },

  invalidAccountMessage: {
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
    display: "flex",
    flexDirection: "column" as const
  },

  formBackground: {
    backgroundColor: colors.farm_gray15,
    padding: "15px 10px"
  },

  endIcon: {
    top: 0,
    right: 0,
    margin: "10px"
  },

  width100: {
    width: "100%"
  },

  mt10: {
    marginTop: "10px"
  },

  colerRed: {
    color: colors.farm_red600
  },

  colerGray200: {
    color: colors.farm_gray200
  },

  noticeContainer: {
    padding: "15px 10px",
  },

  noticeLabel: {
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    marginBottom: 6,
    marginTop: 10
  },

  agreementLabel: {
    color: colors.farm_black,
    marginTop: "30px",
  },

  postEndIcon: {
    position: "absolute" as const,
    top: 0,
    right: 0,
    margin: "10px"
  },

  selectUsersContainer: {
    alignItems: "center",
    justifyContent: "center",
  },

  mt30: {
    marginTop: "30px",
  },
};
